import React from 'react';
import { auth as adminAuth } from '../../admin-auth/adminAppAuth';
import { fetchAdminMe } from '../../api/adminMeApi';
import MagicLinkToggle from './security/MagicLinkToggle';
import { updateMyAdminProfile } from './security/magicLinkSettingsApi';
import './styles/settings.css';
import { useNotification } from '../../../context/NotificationContext';

const formatPhoneNumberInput = (value = '') => {
  const digits = value.replace(/\D/g, '');
  const normalizedDigits =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  const limitedDigits = normalizedDigits.slice(0, 10);

  if (limitedDigits.length <= 3) return limitedDigits;
  if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  }
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
};

const Settings = () => {
  const { showToast } = useNotification();
  const [showPasswordForm, setShowPasswordForm] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  
  // Profile state
  const [admin, setAdmin] = React.useState(null);
  const [profileLoading, setProfileLoading] = React.useState(true);
  const [profileSaving, setProfileSaving] = React.useState(false);
  const [profileEditing, setProfileEditing] = React.useState(false);
  const [profileError, setProfileError] = React.useState('');
  const [profileSuccess, setProfileSuccess] = React.useState('');
  const [profileData, setProfileData] = React.useState({
    firstName: '',
    lastName: '',
    phone: '',
    title: '',
    department: '',
    bio: ''
  });

  const handleSave = () => {
    // Here you would save maintenance mode and password changes
    showToast('Settings saved successfully!', 'success');
    setShowPasswordForm(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // App-level settings (maintenance/support hours) moved to dedicated App Settings page

  // Profile management functions
  React.useEffect(() => {
    const fetchAdminProfile = async () => {
      try {
        const currentUser = adminAuth.currentUser;
        if (!currentUser) {
          setProfileError('No admin user logged in');
          setProfileLoading(false);
          return;
        }

        const profile = await fetchAdminMe();
        setAdmin(profile);
          
          // Initialize form data with existing values or defaults
          setProfileData({
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            phone: formatPhoneNumberInput(profile.phone || ''),
            title: profile.title || '',
            department: profile.department || '',
            bio: profile.bio || ''
          });
      } catch (err) {
        console.error('Error fetching admin profile:', err);
        setProfileError('Failed to load admin profile');
      } finally {
        setProfileLoading(false);
      }
    };

    fetchAdminProfile();
  }, []);

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: name === 'phone' ? formatPhoneNumberInput(value) : value
    }));
  };

  const handleProfileEdit = () => {
    setProfileEditing(true);
    setProfileError('');
    setProfileSuccess('');
  };

  const handleProfileCancel = () => {
    setProfileEditing(false);
    // Reset form data to original values
    setProfileData({
      firstName: admin?.firstName || '',
      lastName: admin?.lastName || '',
      phone: formatPhoneNumberInput(admin?.phone || ''),
      title: admin?.title || '',
      department: admin?.department || '',
      bio: admin?.bio || ''
    });
    setProfileError('');
    setProfileSuccess('');
  };

  const handleProfileSave = async () => {
    try {
      setProfileSaving(true);
      setProfileError('');
      setProfileSuccess('');

      const currentUser = adminAuth.currentUser;
      if (!currentUser) {
        throw new Error('No admin user logged in');
      }

      const updateData = {
        ...profileData,
        updatedAt: new Date()
      };

      await updateMyAdminProfile(profileData);
      
      // Update local state
      setAdmin(prev => ({
        ...prev,
        ...updateData
      }));
      
      setProfileEditing(false);
      setProfileSuccess('Profile updated successfully!');
    } catch (err) {
      console.error('Error updating admin profile:', err);
      
      // Provide friendly error messages based on error type
      let errorMessage = 'Failed to update profile';
      
      if (err?.code === 'permission-denied' || err?.message?.includes('Not authorized')) {
        errorMessage = 'You do not have permission to update your profile. Please contact an administrator.';
      } else if (err?.code === 'unauthenticated') {
        errorMessage = 'Your session has expired. Please sign out and sign back in.';
      } else if (err?.message) {
        // Use the error message if it's user-friendly, otherwise use default
        errorMessage = err.message.includes('Failed to update') 
          ? err.message 
          : 'Unable to save your changes. Please try again.';
      }
      
      setProfileError(errorMessage);
    } finally {
      setProfileSaving(false);
    }
  };

  const getInitials = () => {
    const firstName = admin?.firstName || '';
    const lastName = admin?.lastName || '';
    const email = admin?.email || '';
    
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName[0].toUpperCase();
    } else if (email) {
      return email[0].toUpperCase();
    }
    return 'A';
  };

  const getDisplayName = () => {
    const firstName = admin?.firstName || '';
    const lastName = admin?.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    return admin?.email || 'Admin User';
  };

  return (
    <div className="admin-page adminSettings" role="main" aria-label="Settings">
      <div className="adminSettingsShell">
        <div className="adminSettingsHeader">
          <div>
            <h1 className="adminSettingsTitle">Settings</h1>
            <p className="adminSettingsSub">Manage your admin account and profile.</p>
          </div>
        </div>

        <div className="adminSettingsGrid">
          {/* Profile settings */}
          <section className="adminCard" aria-label="Profile settings">
            <div className="adminCardHeader">
              <div className="adminCardTitle">
                <h2>Profile</h2>
              </div>
            </div>

            {profileLoading ? (
              <p className="adminSettingsSub" style={{ margin: 0 }}>
                Loading profile…
              </p>
            ) : (
              <>
                <div className="profileTop">
                  <div className="profileIdentity">
                    <div className="avatar" aria-hidden="true">
                      {getInitials()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p className="profileName" title={getDisplayName()}>
                        {getDisplayName()}
                      </p>
                      <p className="profileRole">{admin?.title || 'Administrator'}</p>
                    </div>
                  </div>

                  {!profileEditing ? (
                    <button type="button" className="btn" onClick={handleProfileEdit}>
                      Edit
                    </button>
                  ) : (
                    <div className="btnRow">
                      <button type="button" className="btn" onClick={handleProfileCancel} disabled={profileSaving}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btnPrimary"
                        onClick={handleProfileSave}
                        disabled={profileSaving}
                      >
                        {profileSaving ? (
                          <>
                            <span className="spinner" aria-hidden="true" /> Saving…
                          </>
                        ) : (
                          'Save'
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {profileError ? (
                  <div className="alert alertError" role="alert" aria-live="polite" style={{ marginTop: 12 }}>
                    {profileError}
                  </div>
                ) : null}

                {profileSuccess ? (
                  <div className="alert alertSuccess" role="status" aria-live="polite" style={{ marginTop: 12 }}>
                    {profileSuccess}
                  </div>
                ) : null}

                <div className="adminDivider" />

                <div className="formGrid">
                  <div className="field">
                    <label htmlFor="firstName">First name</label>
                    <input
                      id="firstName"
                      className="input"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleProfileInputChange}
                      disabled={!profileEditing}
                      autoComplete="given-name"
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="lastName">Last name</label>
                    <input
                      id="lastName"
                      className="input"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleProfileInputChange}
                      disabled={!profileEditing}
                      autoComplete="family-name"
                    />
                  </div>

                  <div className="field span2">
                    <label htmlFor="email">Email</label>
                    <input id="email" className="input" value={admin?.email || ''} disabled />
                  </div>

                  <div className="field">
                    <label htmlFor="phone">Phone</label>
                    <input
                      id="phone"
                      className="input"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleProfileInputChange}
                      disabled={!profileEditing}
                      placeholder="(555) 555-5555"
                      maxLength={14}
                      inputMode="tel"
                      autoComplete="tel"
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="title">Job title</label>
                    <input
                      id="title"
                      className="input"
                      name="title"
                      value={profileData.title}
                      onChange={handleProfileInputChange}
                      disabled={!profileEditing}
                      autoComplete="organization-title"
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="department">Department</label>
                    <input
                      id="department"
                      className="input"
                      name="department"
                      value={profileData.department}
                      onChange={handleProfileInputChange}
                      disabled={!profileEditing}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="memberSince">Member since</label>
                    <input
                      id="memberSince"
                      className="input"
                      value={admin?.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      disabled
                    />
                  </div>

                  <div className="field span2">
                    <label htmlFor="bio">Bio</label>
                    <textarea
                      id="bio"
                      className="textarea"
                      name="bio"
                      value={profileData.bio}
                      onChange={handleProfileInputChange}
                      disabled={!profileEditing}
                      placeholder="Tell us about yourself…"
                      rows={4}
                    />
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Account settings */}
          <section className="adminCard" aria-label="Account settings">
            <div className="adminCardHeader">
              <div className="adminCardTitle">
                <h2>Account</h2>
              </div>
            </div>

            <div className="rows">
              <MagicLinkToggle
                value={admin?.requireMagicLink === true}
                method={admin?.twoFactorMethod === 'sms' ? 'sms' : 'email'}
                disabled={!adminAuth.currentUser}
                loading={profileLoading}
                onChange={(next) => setAdmin((prev) => ({ ...(prev || {}), requireMagicLink: next }))}
                onMethodChange={(nextMethod) => setAdmin((prev) => ({ ...(prev || {}), twoFactorMethod: nextMethod }))}
              />

              <div className="settingsRow">
                <div>
                  <p className="settingsRowTitle">Password</p>
                  <p className="settingsRowSub">Update your account password.</p>
                </div>
                <button
                  type="button"
                  className="btn btnGhost"
                  onClick={() => setShowPasswordForm((v) => !v)}
                >
                  {showPasswordForm ? 'Close' : 'Update password'}
                </button>
              </div>
            </div>

            {showPasswordForm ? (
              <>
                <div className="adminDivider" />
                <div className="formGrid" aria-label="Password form">
                  <div className="field span2">
                    <label htmlFor="currentPassword">Current password</label>
                    <input
                      id="currentPassword"
                      className="input"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="newPassword">New password</label>
                    <input
                      id="newPassword"
                      className="input"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="confirmPassword">Confirm new password</label>
                    <input
                      id="confirmPassword"
                      className="input"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="span2" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                    <button type="button" className="btn btnPrimary" onClick={handleSave}>
                      Save changes
                    </button>
                  </div>

                  <p className="inlineHint span2">
                    Note: This screen currently shows the UI flow only (password update logic is not wired here yet).
                  </p>
                </div>
              </>
            ) : null}
          </section>
        </div>
      </div>

    </div>
  );
};

export default Settings; 